import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// localStorage keys.
const REF_KEY = 'snapchart_ref_code';      // referrer code (e.g. "ABC123")
const REF_TS_KEY = 'snapchart_ref_ts';     // timestamp set, for 30-day expiry
const SESSION_KEY = 'snapchart_visitor_session'; // anonymous device id
const REF_TTL_DAYS = 30;

// Generate-or-reuse a stable visitor session ID for anti-double-count
// dedup. Stored in localStorage so refreshing the page on the same device
// doesn't inflate the click count. Anonymous; no IP, no PII.
function getOrCreateSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto?.randomUUID && crypto.randomUUID()) ||
           `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch { return null; }
}

// Returns the stored ref code if it's still within the 30-day window, else
// clears it. Used by Register.jsx to attribute the signup back to the
// referrer at sign-up time.
export function getStoredRefCode() {
  try {
    const code = localStorage.getItem(REF_KEY);
    const ts = Number(localStorage.getItem(REF_TS_KEY));
    if (!code || !ts) return null;
    if (Date.now() - ts > REF_TTL_DAYS * 86400000) {
      localStorage.removeItem(REF_KEY);
      localStorage.removeItem(REF_TS_KEY);
      return null;
    }
    return code;
  } catch { return null; }
}

export function clearStoredRefCode() {
  try {
    localStorage.removeItem(REF_KEY);
    localStorage.removeItem(REF_TS_KEY);
  } catch { /* noop */ }
}

// Mount once at the app root. On every page load:
//   1. Reads `?ref=<code>` from the URL.
//   2. If present, stores it in localStorage with a 30-day TTL.
//   3. Logs the click via the public RPC `log_share_click` (fire-and-forget,
//      dedup is enforced server-side per 24h-window).
//
// Never blocks render. Never throws.
// Sanitize a UTM value: trim, lowercase (except utm_content which stays
// case-preserving in case it's a tweet_id), strip anything weird.
// Max 64 chars — attribution table has TEXT columns but no reason to
// swallow arbitrarily long payloads.
function cleanUtm(v, { preserveCase = false } = {}) {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (!preserveCase) s = s.toLowerCase();
  s = s.replace(/[^a-zA-Z0-9._\-]/g, '').slice(0, 64);
  return s || null;
}

export function useRefCapture() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (!ref) return;

      // Sanitize: alphanumeric only, 4-16 chars. Matches the SQL contract.
      const cleaned = String(ref).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
      if (cleaned.length < 4) return;

      // Store for downstream signup attribution.
      localStorage.setItem(REF_KEY, cleaned);
      localStorage.setItem(REF_TS_KEY, String(Date.now()));

      // Extract UTM params so the RPC records where the click came from.
      // Any/all can be absent — the RPC accepts NULLs.
      const utmSource   = cleanUtm(params.get('utm_source'));
      const utmMedium   = cleanUtm(params.get('utm_medium'));
      const utmCampaign = cleanUtm(params.get('utm_campaign'));
      const utmContent  = cleanUtm(params.get('utm_content'), { preserveCase: true });

      // Log the click. Anonymous-callable; do not await.
      const sessionId = getOrCreateSessionId();
      const ua = (navigator?.userAgent || '').slice(0, 200);
      supabase.rpc('log_share_click', {
        p_ref_code:           cleaned,
        p_visitor_session_id: sessionId,
        p_user_agent:         ua,
        p_utm_source:         utmSource,
        p_utm_medium:         utmMedium,
        p_utm_campaign:       utmCampaign,
        p_utm_content:        utmContent,
      }).then(() => {}, (err) => {
        console.warn('[ref-capture] log_share_click failed (non-fatal):', err?.message);
      });
    } catch (err) {
      console.warn('[ref-capture] non-fatal error:', err?.message);
    }
  }, []);
}
